import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { toast } from "@/shared/hooks/use-toast";
import { errorText } from "@/shared/lib/apiErrors";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Label } from "@/shared/ui/label";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { Switch } from "@/shared/ui/switch";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";

import {
  flowService,
  isInsufficientCredits,
  type ImageQuote,
  type SourcesView,
} from "../api/flowService";
import { websiteService, type SiteDetail, type TemplateSummary } from "../api/websiteService";
import {
  canStart,
  doneSummary,
  INTRO_MAX,
  needsConfirm,
  OWN_IMAGES_MAX,
  quoteText,
  shortfallText,
} from "../model/generateFlow";
import { isLaunched, sitePath } from "../model/siteStep";
import { compressImage } from "./components/flow/imageFile";
import { ProxiedImage } from "./components/host/ProxiedImage";
import { GenerateProgress } from "./components/flow/GenerateProgress";
import { QuoteConfirmDialog } from "./components/flow/QuoteConfirmDialog";
import { useFlowJob } from "./hooks/useFlowJob";
import { SiteShell } from "./SiteShell";

interface OwnImage {
  file: File;
  url: string;
}

const card = cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS);

/**
 * ① 生成(1010 流程改造,一屏):模板(可改 / AI 按行业推荐)+ 自动读取的已有资料 + 可选补充介绍 / 自有图 +
 * 「AI 配图」开关与报价 →「生成官网」一次完成:采集答案自动填充 → 出稿 → 配图 → 发布到免费子域名作为预览。
 *
 * ★ 事实红线不变:电话 / 地址 / 资质 / 价格只来自资料或客户输入;缺的不编造、不阻断,进「补充建议」清单(预览调整页)。
 * ★ AI 配图开且要生成图时先弹报价确认;余额不足在报价时就拦,不开始任务。
 * ★ 原「资料采集」「内容编辑」保留为「高级编辑」入口(站点页标签里)。
 * ★ 进度面板在页面顶部(开始后滚过去、高亮);生成完(含 partial)toast 小结并自动跳预览调整页。
 */
export default function GeneratePage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [materials, setMaterials] = useState<SourcesView | null>(null);
  const [materialsError, setMaterialsError] = useState<string | null>(null);
  const [templateCode, setTemplateCode] = useState<string | null>(null);
  const [intro, setIntro] = useState("");
  const [images, setImages] = useState<OwnImage[]>([]);
  const [aiImages, setAiImages] = useState(true);
  const [quote, setQuote] = useState<ImageQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoting, setQuoting] = useState(false);

  const { phase, send, run } = useFlowJob(id, {
    resume: true,
    onSettled: (t) => {
      if (t.status === "failed") return;
      // 生成完自动去预览调整(1010 演示机反馈:停在本页客户不知道下一步)
      const summary = doneSummary(t);
      toast({ title: "官网已生成", ...(summary ? { description: summary } : {}) });
      navigate(sitePath(id, "preview"));
    },
  });

  // 开始生成后把进度面板滚到视口里(面板在页面顶部;小屏 / 页面已下滚时也能看到动静)
  const progressRef = useRef<HTMLDivElement>(null);
  const running = phase.kind === "running";
  useEffect(() => {
    if (running) progressRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [running]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const s = await websiteService.getSite(id);
      setSite(s);
      setTemplateCode((cur) => cur ?? s.templateCode);
    } catch (e) {
      setError(errorText(e));
      return;
    }
    websiteService
      .listTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]));
    setMaterialsError(null);
    flowService
      .sources(id)
      .then((src) => {
        setMaterials(src);
        // 上次填的补充介绍回填(用户已经开始写就不盖)
        if (src.intro) setIntro((cur) => (cur ? cur : (src.intro ?? "")));
      })
      .catch((e) => setMaterialsError(errorText(e)));
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // 报价跟着模板 / 开关 / 自有图张数走(AI 配图关时不请求,不扣费)。自有图 = 上次已传的 + 这次新选的。
  const prevGallery = materials?.galleryImages ?? [];
  const ownCount = Math.min(OWN_IMAGES_MAX, prevGallery.length + images.length);
  useEffect(() => {
    if (!site || !aiImages) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    setQuoteError(null);
    flowService
      .quote(id, {
        aiImages: true,
        galleryCount: ownCount,
        ...(templateCode && templateCode !== site.templateCode ? { templateCode } : {}),
      })
      .then((q) => {
        if (!cancelled) setQuote(q);
      })
      .catch((e) => {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(errorText(e));
        }
      })
      .finally(() => {
        if (!cancelled) setQuoting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, site, aiImages, ownCount, templateCode]);

  // 预览图的 object url 用完回收
  useEffect(() => () => images.forEach((i) => URL.revokeObjectURL(i.url)), [images]);

  const hostTemplates = useMemo(() => {
    const host = templates.filter((t) => t.hostTemplate);
    return host.length > 0 ? host : templates;
  }, [templates]);

  if (error) return <QueryErrorBanner isError message={error} onRetry={() => void load()} />;
  if (!site) return <Skeleton className="h-64 w-full" />;

  const locked = running || phase.kind === "succeeded";
  const rec = materials?.recommended?.[0];
  const recommended = rec ? { code: rec.code, name: rec.name, reason: rec.reason } : undefined;
  const currentTemplate = templates.find((t) => t.code === templateCode);
  // 正式上线前(launched=false)都能换模板(api 删旧预览站、新模板重新生成);上线后即将上线
  const templateLocked = isLaunched(site);
  const blocked = aiImages && quote !== null && !canStart(quote);
  const previewPath = sitePath(id, "preview");

  const start = () =>
    run(async () => {
      const imageUrls: string[] = [...prevGallery];
      for (const img of images) {
        const data = await compressImage(img.file);
        const { url } = await flowService.uploadGallery(id, data);
        imageUrls.push(url);
      }
      try {
        return await flowService.generate(id, {
          ...(templateCode && templateCode !== site.templateCode ? { templateCode } : {}),
          ...(intro.trim() ? { intro: intro.trim() } : {}),
          ...(imageUrls.length > 0 ? { galleryImages: imageUrls.slice(0, OWN_IMAGES_MAX) } : {}),
          aiImages,
          mode: "full",
        });
      } catch (e) {
        if (isInsufficientCredits(e)) {
          throw new Error("积分不足,先关掉「AI 配图」生成,或充值后再来");
        }
        throw e;
      }
    });

  const onGenerate = () => {
    if (blocked) return;
    if (needsConfirm(aiImages, quote) && quote) send({ type: "ASK_CONFIRM", quote });
    else void start();
  };

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const room = OWN_IMAGES_MAX - prevGallery.length - images.length;
    const picked = Array.from(list)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, Math.max(0, room))
      .map((file) => ({ file, url: URL.createObjectURL(file) }));
    if (picked.length > 0) setImages((prev) => [...prev, ...picked]);
  };

  return (
    <SiteShell
      site={site}
      pageTitle="生成"
      subtitle="读取已有资料,一键生成整站文字与配图,发布到免费子域名先预览"
    >
      <div ref={progressRef} className="scroll-mt-4" data-progress-anchor="">
        <GenerateProgress
          phase={phase}
          highlight
          onRetry={() => send({ type: "RETRY" })}
          done={
            <Button size="sm" onClick={() => navigate(previewPath)}>
              去预览调整
            </Button>
          }
        />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
        <fieldset disabled={locked} className="min-w-0 space-y-4" aria-label="生成设置">
          <section aria-label="模板" className={card}>
            <h2 className="text-sm font-semibold text-foreground">模板</h2>
            <div className="flex flex-wrap items-center gap-2">
              <select
                aria-label="选择模板"
                disabled={templateLocked}
                className="h-9 min-w-[200px] rounded-md border border-input bg-background px-2 text-sm"
                value={templateCode ?? site.templateCode}
                onChange={(e) => setTemplateCode(e.target.value)}
              >
                {!hostTemplates.some((t) => t.code === (templateCode ?? site.templateCode)) && (
                  <option value={templateCode ?? site.templateCode}>{site.templateName}</option>
                )}
                {hostTemplates.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </select>
              {currentTemplate?.previewUrl && (
                <a
                  href={currentTemplate.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline"
                >
                  看演示
                </a>
              )}
            </div>
            {templateLocked && (
              <p className="text-2xs text-muted-foreground">
                这个站已经正式上线,换模板即将上线;现在可以用同一模板重新生成。
              </p>
            )}
            {recommended ? (
              <p className="flex flex-wrap items-center gap-2 text-xs" data-template-recommend="">
                <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
                <span>
                  AI 按行业推荐:<b>{recommended.name}</b>
                  {recommended.reason ? `(${recommended.reason})` : ""}
                </span>
                {recommended.code === (templateCode ?? site.templateCode) ? (
                  <span className="text-2xs text-muted-foreground">当前就是这套</span>
                ) : (
                  !templateLocked && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      onClick={() => setTemplateCode(recommended.code)}
                    >
                      用这套
                    </Button>
                  )
                )}
              </p>
            ) : (
              materials && (
                <p className="text-2xs text-muted-foreground" data-template-recommend="none">
                  暂无按行业推荐,可在
                  <Link to="/ai-website/templates" className="mx-0.5 text-primary hover:underline">
                    模板市场
                  </Link>
                  挑选
                </p>
              )
            )}
            {templateCode !== site.templateCode && (
              <p className="text-2xs text-muted-foreground">换模板后用同一份资料在新模板上生成。</p>
            )}
          </section>

          <section aria-label="已读取的资料" className={card}>
            <h2 className="text-sm font-semibold text-foreground">已读取的资料</h2>
            <p className="text-2xs text-muted-foreground">
              从知识库、产品库、品牌资产、企业档案自动读取。电话、地址、资质、价格只用资料里有的,缺的不编,生成后在「补充建议」里补。
            </p>
            {materialsError ? (
              <p role="alert" className="text-xs text-destructive">
                资料读取失败:{materialsError}(仍可生成,只用你在下面补充的内容)
              </p>
            ) : !materials ? (
              <Skeleton className="h-8 w-full" />
            ) : (
              <ul className="flex flex-wrap gap-2" aria-label="资料清单">
                {materials.items.map((m) => (
                  <li
                    key={m.key}
                    data-material={m.key}
                    data-ok={m.ok ? "1" : "0"}
                    title={m.source}
                    className={cn(
                      "flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs",
                      m.ok
                        ? "border-success/40 bg-success/5 text-foreground"
                        : "border-border text-muted-foreground"
                    )}
                  >
                    {m.ok ? (
                      <Check className="h-3 w-3 text-success" aria-hidden />
                    ) : (
                      <X className="h-3 w-3" aria-hidden />
                    )}
                    {m.label}
                    {m.detail && <span className="text-muted-foreground">· {m.detail}</span>}
                    <span className="sr-only">{m.ok ? "已读取" : "没有"}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-label="补充(可选)" className={card}>
            <div className="space-y-1.5">
              <Label htmlFor="flow-intro">补充一段公司介绍(可选)</Label>
              <Textarea
                id="flow-intro"
                rows={5}
                maxLength={INTRO_MAX}
                value={intro}
                placeholder="比如:做什么、服务谁、有什么优势。资料里已有的可以不写。"
                onChange={(e) => setIntro(e.target.value.slice(0, INTRO_MAX))}
              />
              <p className="text-right text-2xs text-muted-foreground">
                {intro.length}/{INTRO_MAX}
              </p>
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">
                上传图片(可选,≤{OWN_IMAGES_MAX} 张,优先用在官网上)
              </span>
              <ul className="flex flex-wrap gap-2" aria-label="已选图片">
                {images.map((img, i) => (
                  <li
                    key={img.url}
                    className="relative h-16 w-16 overflow-hidden rounded border border-border"
                  >
                    <img
                      src={img.url}
                      alt={`自有图 ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      aria-label={`移除自有图 ${i + 1}`}
                      className="absolute right-0.5 top-0.5 rounded-full bg-background/90 p-0.5"
                      onClick={() => setImages((prev) => prev.filter((x) => x !== img))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
                {prevGallery.map((url, i) => (
                  <li
                    key={url}
                    className="h-16 w-16 overflow-hidden rounded border border-border"
                    title="上次上传的图"
                  >
                    <ProxiedImage
                      value={url}
                      alt={`已上传 ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </li>
                ))}
                {prevGallery.length + images.length < OWN_IMAGES_MAX && (
                  <li>
                    <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center gap-0.5 rounded border border-dashed border-border text-2xs text-muted-foreground hover:border-primary">
                      <ImagePlus className="h-4 w-4" aria-hidden />
                      添加
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="sr-only"
                        aria-label="添加图片"
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </li>
                )}
              </ul>
            </div>
          </section>
        </fieldset>

        <aside className="space-y-4">
          <section aria-label="AI 配图" className={card}>
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="flow-ai-images" className="text-sm font-semibold">
                AI 配图
              </Label>
              <Switch
                id="flow-ai-images"
                checked={aiImages}
                disabled={locked}
                onCheckedChange={setAiImages}
              />
            </div>
            <p className="text-2xs text-muted-foreground">
              配图顺序:你上传的图 → AI 生成 → 借用本站已有的图。模板原图只用在装饰背景 /
              图标上,不会出现原模板企业的门头、产品。
            </p>
            {aiImages ? (
              quoting ? (
                <p className="text-xs text-muted-foreground">正在估算…</p>
              ) : quoteError ? (
                <p role="alert" className="text-xs text-destructive">
                  报价失败:{quoteError}
                </p>
              ) : quote ? (
                <div className="space-y-1" data-quote="">
                  <p className="text-sm font-medium text-foreground">{quoteText(quote)}</p>
                  {quote.message && (
                    <p className="text-2xs text-muted-foreground">{quote.message}</p>
                  )}
                  {!canStart(quote) && (
                    <p role="alert" className="text-xs text-destructive">
                      {shortfallText(quote)}
                    </p>
                  )}
                </div>
              ) : null
            ) : (
              <p className="text-xs text-muted-foreground">
                不用 AI 配图:只用你上传的图和模板装饰图,不扣积分。
              </p>
            )}
          </section>

          {site.hasDraft && phase.kind === "idle" && (
            <p role="note" className="text-2xs text-muted-foreground">
              这个站已经生成过,重新生成会替换当前草稿(已上线的版本不受影响)。
            </p>
          )}

          <Button
            className="w-full"
            size="lg"
            disabled={locked || blocked || (aiImages && (quoting || !quote))}
            onClick={onGenerate}
          >
            {running ? <Loader2 className="animate-spin" /> : <Sparkles />}
            生成官网
          </Button>

          <p className="text-2xs text-muted-foreground">
            想逐项核对资料、逐块改文字?用
            <Link to={sitePath(id, "collect")} className="mx-0.5 text-primary hover:underline">
              高级编辑
            </Link>
            。
          </p>
        </aside>
      </div>

      <QuoteConfirmDialog
        quote={phase.kind === "confirming" ? phase.quote : null}
        onCancel={() => send({ type: "CANCEL" })}
        onConfirm={() => void start()}
      />
    </SiteShell>
  );
}
